import type { RecurringTournament } from '@ps/web-shared-api-types/v0/SwHomeGames';
import { Translation } from '@ps/web-shared-components';
import { format } from 'date-fns';
import React, { useMemo } from 'react';
import { useField } from 'react-final-form';
import { useSelector } from 'react-redux';

import Numbers from '@swf/components/Inputs/Numbers';
import StarsDropDown from '@swf/components/Inputs/StarsDropDown';
import Text from '@swf/components/Inputs/Text';
import { select } from '@swf/core/store';
import { DateTimePicker } from '@swf/poker';

import occurrenceRangeOptions from '../utils/occurrenceRangeOptions';
import { RangeOption, addToDate } from '../utils/utils';

import { TXTCLI_EndX, TXTCLI_End_Date, TXTWCL_Occurrences } from './Range.messages.json';
import styles from './Range.scss';

const startOfTheDay = (date: Date) => format(new Date(date).setHours(0, 0, 0, 0), 'yyyy-MM-dd');

const Range: React.FC = () => {
  const isMobileDevice = useSelector(select.device.isMobile);
  const {
    input: { value: startDate }
  } = useField('startDate');
  const dateStart = useMemo(() => addToDate.HG_RECURRING_DAILY(startDate, 1), [startDate]);
  const {
    input: { value: occurrenceRange }
  } = useField('occurrenceRange', { defaultValue: RangeOption.Never });
  useField('nbOccurences', { defaultValue: 1 as RecurringTournament['nbOccurences'] });
  const {
    input: { value: endDate }
  } = useField('endDate', { defaultValue: dateStart });

  const displayDatePicker = occurrenceRange === RangeOption.On && !isMobileDevice;
  const displayNativeDatePicker = occurrenceRange === RangeOption.On && isMobileDevice;

  return (
    <div className={styles.container}>
      <div className={styles.occurrenceRange}>
        <StarsDropDown
          className={styles.dropdown}
          label={TXTCLI_EndX}
          id="occurrenceRange"
          options={occurrenceRangeOptions}
        />
      </div>
      {occurrenceRange === RangeOption.After && (
        <div className={styles.after}>
          <Numbers
            hideLabel
            className={styles.input}
            label="Occurrences Number"
            min={1}
            showValidationTick={false}
            id="nbOccurences"
          />
          <Translation tagName="div" {...TXTWCL_Occurrences} />
        </div>
      )}
      {displayDatePicker && (
        <DateTimePicker
          hideLabel
          id="endDate"
          label="End Date"
          hideDateFormat
          className={styles.picker}
          minDate={dateStart}
          defaultDate={endDate}
        />
      )}
      {displayNativeDatePicker && (
        <Text
          hideLabel
          id="endDate"
          name="startDate"
          className={styles.mobilePicker}
          type="date"
          min={startOfTheDay(dateStart)}
          defaultValue={startOfTheDay(endDate)}
          label={TXTCLI_End_Date}
        />
      )}
    </div>
  );
};

export default Range;
